import { useState } from "react";
import { Typography, Tooltip, Box, Snackbar, Chip, Divider, useMediaQuery, useTheme } from '@mui/material';
import { generatePastelColor, generateSaturatedColor } from '../../Utils/colorUtils';
import { getFileExtension,GetFileNameWithoutExtension } from '../../Utils/fileUtils';
import { useTranslation } from "react-i18next";
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

export const shouldShowFilenameForExtension = (fileFormat) => fileFormat !== 'DATAUNFOLD';
export const shouldShowExtensionForExtension = (fileFormat) => fileFormat !== 'DATAUNFOLD';
export const formatTimeEventTokenCount = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? new Intl.NumberFormat().format(numericValue) : '0';
};

function TimeeEventFilename({ path, folder, className, sx, aiTokenUsage = null }) {
    const { t } = useTranslation();
    const [documentName, setDocumentName] = useState(path);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const fileFormat = getFileExtension(path);
    const theme = useTheme();
    const isNarrowView = useMediaQuery(theme.breakpoints.down('md'));
    const showPath = !isNarrowView;
    const showExtension = !isNarrowView;
    const totalTokens = Number(aiTokenUsage?.total_tokens || 0);
    const inputTokens = Number(aiTokenUsage?.input_tokens || 0);
    const outputTokens = Number(aiTokenUsage?.output_tokens || 0);
    const requestCount = Number(aiTokenUsage?.request_count || 0);
    const hasTokenUsage = Number.isFinite(totalTokens) && totalTokens > 0;

    if (isNarrowView) {
        return null;
    }

    // Theme colors
    const primaryColor = '#5757b0';
    const lightPurple = '#f0f0ff';

    // List of allowed extensions
    const allowedExtensions = [
        'PDF', 'DOC', 'EML', 'JPG', 'JPEG', 'PNG','TIFF','HEIC','BMP',"AVIF",
        'TXT', 'CSV', 'MSG', 'INI', 'PPTX', 'XLSX',"HL7",
        'XLSM', 'DOCX', 'XLS', 'WEBP','ZIP',"M4A","MP3","WEBM","MP4","MPGA","WAV","MPEG"
    ];
    const sanitizedPath = path ? path.replace(folder, '') : '';

    const splitPath = sanitizedPath ? sanitizedPath.split(/[\\/]/) : []; // Split by either '/' or '\' to handle different OSes
    const lastThreeFolders = splitPath.length > 0 ? splitPath.slice(-3) : []; // Get the last three folders or filename
    const fileName = lastThreeFolders.pop(); // Extract the file name
    const nameWithoutExtension = fileName ? GetFileNameWithoutExtension(fileName) : '';
    const showFilenameInSpecialLayout = shouldShowFilenameForExtension(fileFormat) && Boolean(nameWithoutExtension);
    const showExtensionInSpecialLayout = shouldShowExtensionForExtension(fileFormat);

    // Determine the color of the file extension text
    const extensionTextColor = allowedExtensions.includes(fileFormat) ? 'white' : '#FF6666';    // Function to render the complete path in a single line
    const renderCompletePath = () => {
        const displayPath = lastThreeFolders.length > 0 ? lastThreeFolders.join(' / ') : '';
        const hasMoreFolders = splitPath.length > 3;
        
        return (
            <Box
                onClick={handleCopyClick}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
                    border: `2px solid ${primaryColor}20`,
                    borderRadius: '10px',
                    padding: '6px 12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    minWidth: 0,
                    maxWidth: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 16px rgba(87, 87, 176, 0.15)',
                        borderColor: primaryColor,
                        '& .copy-icon': {
                            opacity: 1,
                            transform: 'translateX(0)',
                        },
                    },
                    '&:active': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 8px rgba(87, 87, 176, 0.2)',
                    },
                }}
            >
                <FolderIcon 
                    sx={{ 
                        fontSize: '16px', 
                        color: primaryColor, 
                        marginRight: '8px',
                        opacity: 0.8 
                    }} 
                />
                
                {/* Path display */}
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    flex: 1, 
                    overflow: 'hidden',
                    gap: '4px'
                }}>
                    {showPath && hasMoreFolders && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: '#999',
                                fontSize: '0.7rem',
                                fontWeight: '400',
                            }}
                        >
                            ...
                        </Typography>
                    )}
                    
                    {showPath && displayPath && (
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: '0.75rem',
                                fontWeight: '400',
                                color: '#666',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                marginRight: '6px',
                            }}
                        >
                            {displayPath}
                        </Typography>
                    )}
                    
                    {showPath && displayPath && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: '#999',
                                fontSize: '0.7rem',
                                fontWeight: '400',
                                marginRight: '6px',
                            }}
                        >
                            /
                        </Typography>
                    )}
                    
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            color: '#333',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                        }}
                    >
                        {nameWithoutExtension}
                    </Typography>
                </Box>

                {/* Extension badge */}
                {fileFormat && showExtension && (
                    <Box
                        sx={{
                            backgroundColor: allowedExtensions.includes(fileFormat)
                                ? generateSaturatedColor(fileFormat)
                                : '#ff6b6b',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '6px',
                            fontSize: '0.65rem',
                            fontWeight: '600',
                            letterSpacing: '0.5px',
                            textTransform: 'uppercase',
                            marginLeft: '8px',
                            boxShadow: allowedExtensions.includes(fileFormat)
                                ? `0 2px 4px ${generateSaturatedColor(fileFormat)}40`
                                : '0 2px 4px rgba(255, 107, 107, 0.4)',
                        }}
                    >
                        {fileFormat}
                    </Box>
                )}
                
                <ContentCopyIcon 
                    className="copy-icon"
                    sx={{ 
                        fontSize: '14px', 
                        color: primaryColor,
                        opacity: 0,
                        transform: 'translateX(-10px)',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        marginLeft: '8px',
                    }} 
                />
            </Box>
        );    };

    // Handle copying path and showing popup
    const handleCopyClick = () => {
        navigator.clipboard.writeText(path);
        setSnackbarOpen(true);
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };    return (
        <>
            <Tooltip 
                title={
                    <Box sx={{ p: 1 }}>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>
                            {documentName}
                        </Typography>
                        {hasTokenUsage && (
                            <>
                                <Divider sx={{ my: 0.75, borderColor: 'rgba(255,255,255,0.25)' }} />
                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
                                    {t('timeevent.ai_token_usage', 'AI tokens: {{count}}', {
                                        count: formatTimeEventTokenCount(totalTokens),
                                    })}
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>
                                    {t('timeevent.ai_token_usage_breakdown', 'Input: {{input}} · Output: {{output}} · Requests: {{requests}}', {
                                        input: formatTimeEventTokenCount(inputTokens),
                                        output: formatTimeEventTokenCount(outputTokens),
                                        requests: formatTimeEventTokenCount(requestCount),
                                    })}
                                </Typography>
                            </>
                        )}
                    </Box>
                }
                placement="top"
                arrow
            >
                <Box
                    className={className}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'nowrap',
                        gap: '4px',
                        padding: '4px',
                        borderRadius: '12px',
                        minWidth: 0,
                        maxWidth: '100%',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f9f9ff 100%)',
                        border: `1px solid ${primaryColor}10`,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                            boxShadow: '0 2px 8px rgba(87, 87, 176, 0.1)',
                            borderColor: `${primaryColor}20`,
                        },
                        ...sx,
                    }}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}                >
                    {splitPath.some(folder => folder.includes('_dataunfold')) ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
                                border: `2px solid ${primaryColor}20`,
                                borderRadius: '10px',
                                padding: '6px 12px',
                                minWidth: 0,
                                maxWidth: '100%',
                                cursor: 'pointer',
                                '&:hover': {
                                    borderColor: primaryColor,
                                    boxShadow: '0 2px 8px rgba(87, 87, 176, 0.15)',
                                },
                            }}
                            onClick={handleCopyClick}
                        >
                            <InsertDriveFileIcon 
                                sx={{ 
                                    fontSize: '16px', 
                                    color: primaryColor, 
                                    marginRight: '8px',
                                    opacity: 0.8 
                                }} 
                            />
                            {showFilenameInSpecialLayout && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontSize: '0.75rem',
                                        fontWeight: '600',
                                        color: '#333',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        flex: 1,
                                        minWidth: 0,
                                        marginRight: '8px',
                                    }}
                                >
                                    {nameWithoutExtension}
                                </Typography>
                            )}
                            {fileFormat && showExtensionInSpecialLayout && (
                                <Box
                                    sx={{
                                        backgroundColor: allowedExtensions.includes(fileFormat)
                                            ? generateSaturatedColor(fileFormat)
                                            : '#ff6b6b',
                                        color: 'white',
                                        padding: '2px 6px',
                                        borderRadius: '6px',
                                        fontSize: '0.65rem',
                                        fontWeight: '600',
                                        letterSpacing: '0.5px',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    {fileFormat}
                                </Box>
                            )}
                        </Box>
                    ) : (
                        renderCompletePath()
                    )}
                </Box>
            </Tooltip>            {/* Enhanced Snackbar for copy confirmation */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={2000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                sx={{
                    '& .MuiSnackbarContent-root': {
                        backgroundColor: primaryColor,
                        color: 'white',
                        borderRadius: '12px',
                        boxShadow: '0 8px 32px rgba(87, 87, 176, 0.3)',
                        fontWeight: '500',
                    }
                }}
                message={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ContentCopyIcon sx={{ fontSize: '18px' }} />
                        {t('clipboard_copied') || 'Path copied to clipboard!'}
                    </Box>
                }
            />
        </>
    );
}

export default TimeeEventFilename;
